import { Directive, ElementRef, inject, input, OnChanges, Renderer2 } from '@angular/core';

/**
 * Applies Angular's view encapsulation attribute to dynamically injected innerHTML content.
 * This allows normal component-scoped CSS to style innerHTML nodes without ::ng-deep.
 *
 * Usage:
 * ```html
 * <div [appScopedInnerHtml]="htmlContent()"></div>
 * ```
 */
@Directive({
    selector: '[appScopedInnerHtml]'
})
export class ScopedInnerHtmlDirective implements OnChanges {
    private readonly el = inject(ElementRef<HTMLElement>);
    private readonly renderer = inject(Renderer2);

    public readonly appScopedInnerHtml = input.required<string>();

    public ngOnChanges(): void {
        const host = this.el.nativeElement;
        host.innerHTML = this.appScopedInnerHtml();
        this.applyEncapsulationAttribute(host);
    }

    private applyEncapsulationAttribute(host: HTMLElement): void {
        const encapsulationAttr = this.getEncapsulationAttribute(host);
        if (!encapsulationAttr) {
            return;
        }

        this.stampChildren(host, encapsulationAttr);
    }

    private getEncapsulationAttribute(element: HTMLElement): string | null {
        const attrs = element.attributes;
        for (let i = 0; i < attrs.length; i++) {
            const name = attrs[i].name;
            if (name.startsWith('_ngcontent') || name.startsWith('_nghost')) {
                return name;
            }
        }
        return null;
    }

    private stampChildren(parent: HTMLElement, attr: string): void {
        const children = parent.querySelectorAll('*');
        for (let i = 0; i < children.length; i++) {
            this.renderer.setAttribute(children[i], attr, '');
        }
    }
}
